import { Breadcrumb } from '@app/components/layout/page/components/page-breadcrumb';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { DashboardLastUpdated } from '@shared/components/dashboard/last-updated';
import { ReactElement, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { GroupGlobalFilter } from './group-global-filter';
import {
  GROUP_PAGES,
  GROUP_ROUTES,
  GroupSectionKey,
  SECTION_LABELS,
  isSectionUnlocked,
} from '../config/pages';
import { useGroupContext } from '../group-context';

type GroupPageKey = keyof typeof GROUP_PAGES;

type Props = {
  pageKey: GroupPageKey;
  /** When the page's dashboard data was last refreshed — shown beside the global filter. */
  lastRefreshedAt?: string | null;
  children: ReactNode;
};

/**
 * Shared shell for every group dashboard page. Renders the platform PageHeader
 * with the page title, the optional single-crumb section label, and the
 * global org+period filter in the toolbar slot — then defers the body to its
 * children.
 *
 * If the page is gated to a section the group hasn't unlocked, redirects back
 * to `/group` rather than rendering — defence-in-depth against direct URLs
 * landing past the sidebar lock.
 */
export default function GroupPage({
  pageKey,
  lastRefreshedAt,
  children,
}: Props) {
  const config = GROUP_PAGES[pageKey];
  const { groupFeatures, status } = useGroupContext();
  const section: GroupSectionKey | undefined = config.section;

  const breadcrumbs: Breadcrumb[] = section
    ? [{ label: SECTION_LABELS[section], url: '' }]
    : [];

  // PageHeader's `toolbar` prop is typed as ReactElement<PageHeaderToolbarProps>,
  // but the component renders whatever we pass into a right-aligned slot.
  const toolbar = (
    <div className="flex items-center gap-6">
      <DashboardLastUpdated
        lastRefreshedAt={lastRefreshedAt}
        hoverText="Dashboard data is updated daily."
      />
      <GroupGlobalFilter />
    </div>
  ) as ReactElement;

  if (status === 'pending' || status === 'loading') {
    return (
      <Page name={`group-${pageKey}`} status="loading">
        <PageHeader name={config.title} breadcrumbs={breadcrumbs} />
      </Page>
    );
  }

  // Only treat a section as locked once group features have actually loaded.
  // On an account-load error `groupFeatures` is null, which must NOT be read as
  // "locked" — otherwise a transient API failure ejects the user out of the
  // group dashboard instead of surfacing the error in place.
  const locked =
    status === 'success' && section
      ? !isSectionUnlocked(section, groupFeatures)
      : false;
  if (locked) {
    return <Navigate to={GROUP_ROUTES.DASHBOARD} replace />;
  }

  return (
    <Page name={`group-${pageKey}`} status={status}>
      <PageHeader
        name={config.title}
        breadcrumbs={breadcrumbs}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        toolbar={toolbar as any}
      />
      {children}
    </Page>
  );
}
